import React, { createContext, type PropsWithChildren, useContext, useMemo } from 'react';

import invariant from 'tiny-invariant';

import { type GlobalSideNavigationProps } from '../../common/types';

export interface Options extends Pick<
	GlobalSideNavigationProps,
	| 'app'
	| 'appLabel'
	| 'cloudId'
	| 'customizationStore'
	| 'defaultMenuVisibility'
	| 'disableMenuCustomization'
	| 'intlMode'
	| 'isShortcutsStatic'
	| 'networkStorageParams'
	| 'userId'
> {
	// Whether the bottom element is provided
	hasBottomElement?: boolean;
	// Whether level 1 drag & drop is enabled
	l1DragAndDropEnabled: boolean;
}

export const OptionsContext: React.Context<Options | undefined> = createContext<
	Options | undefined
>(undefined);

export type OptionsProviderProps = PropsWithChildren<Options>;

export function OptionsProvider({
	app,
	appLabel,
	children,
	cloudId,
	customizationStore,
	defaultMenuVisibility,
	disableMenuCustomization,
	hasBottomElement = false,
	intlMode = 'async',
	isShortcutsStatic,
	l1DragAndDropEnabled,
	networkStorageParams,
	userId,
}: OptionsProviderProps): React.JSX.Element {
	const options = useMemo(
		() => ({
			app,
			appLabel,
			cloudId,
			customizationStore,
			defaultMenuVisibility,
			disableMenuCustomization,
			hasBottomElement,
			intlMode,
			isShortcutsStatic,
			l1DragAndDropEnabled,
			networkStorageParams,
			userId,
		}),
		[
			app,
			appLabel,
			cloudId,
			customizationStore,
			defaultMenuVisibility,
			disableMenuCustomization,
			hasBottomElement,
			intlMode,
			isShortcutsStatic,
			l1DragAndDropEnabled,
			networkStorageParams,
			userId,
		],
	);

	return <OptionsContext.Provider value={options}>{children}</OptionsContext.Provider>;
}

export const useOptions = (): Options => {
	const options = useContext(OptionsContext);

	invariant(options, 'useOptions must be used inside an initialized OptionsProvider');

	return options;
};
